import { useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { Card } from '@/components/Card';

// Self-service settings panel (webapp): a logged-in user mints a short-lived,
// single-use, human-typable login code ("correct horse battery staple") and
// types it into the login page on another device. Complements the portable
// link for cases where there's no copy/paste channel between devices — the
// code is read off one screen and typed on the other.
interface LoginCodeResult {
  code: string;
  expires_at: number;
}

export function LoginCodePanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<LoginCodeResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Tick while a code is displayed so the countdown stays honest and the
  // code visibly expires instead of lingering as if still valid.
  useEffect(() => {
    if (!result) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [result]);

  async function generate() {
    if (busy) return;
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const r = await fetch('/api/auth/login-code', {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        const text = await r.text();
        throw new Error(text || `request failed (${r.status})`);
      }
      setResult((await r.json()) as LoginCodeResult);
      setNow(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function copyCode() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — the code is selectable text as a fallback.
    }
  }

  const remainingMs = result ? result.expires_at - now : 0;
  const expired = result !== null && remainingMs <= 0;

  return (
    <Card className="space-y-3">
      <h2 className="text-lg font-semibold">One-time login code</h2>
      <p className="text-xs text-slate-600">
        Generate a four-word code, then type it into the “Have a login code?” box on the
        sign-in page of another device. It works once and expires after 15 minutes — handy
        when you can read it off this screen but can’t paste a link.
      </p>

      <Button variant="primary" onClick={() => void generate()} loading={busy}>
        {busy ? 'Generating…' : result ? 'Generate a new code' : 'Generate login code'}
      </Button>

      {result && !expired && (
        <div className="space-y-2 rounded border border-green-200 bg-green-50 p-3 text-xs">
          <div
            className="select-all text-center font-mono text-lg font-semibold tracking-wide text-slate-900"
            data-testid="login-code"
          >
            {result.code}
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-green-800">
              Expires in {formatRemaining(remainingMs)} · single use
            </span>
            <Button size="xs" className="shrink-0" onClick={() => void copyCode()}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      )}
      {expired && (
        <Alert>That code has expired — generate a new one.</Alert>
      )}
      {error && <Alert>{error}</Alert>}
    </Card>
  );
}

function formatRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
